import { Grid, Icon, InputRadioCard, InputRadioGroup, Stack, Text } from "@bookingcom/bui-react";
import { GroupIcon, TransportAirplaneIcon } from "@bookingcom/bui-assets-react/streamline";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { AirlineGroup, AirlineOption } from "../../../types";
import { GroupAccessSection } from "./GroupAccessSection";
import { SingleAirlineSection } from "./SingleAirlineSection";

export type AssignmentMode = "group" | "single";

type AccessSelectorProps = {
  groups: AirlineGroup[];
  airlines: AirlineOption[];
  mode: AssignmentMode;
  onModeChange: (mode: AssignmentMode) => void;
  selectedGroupId: number | null;
  selectedAirlineIds: number[];
  selectedSingleAirlineId: number | null;
  onGroupChange: (groupId: number) => void;
  onAirlineToggle: (airlineId: number) => void;
  onSingleAirlineChange: (airlineId: number) => void;
};

export const AccessSelector = ({
  groups,
  airlines,
  mode,
  onModeChange,
  selectedGroupId,
  selectedAirlineIds,
  selectedSingleAirlineId,
  onGroupChange,
  onAirlineToggle,
  onSingleAirlineChange,
}: AccessSelectorProps) => {
  const i18n = useI18n();
  return (
    <Stack gap={6}>
      <InputRadioGroup
        name="assignment-mode"
        label={i18n.trans(t("flights_airline_portal_add_user_access_type_header"))}
        value={mode}
        onChange={({ value }) => onModeChange(value as AssignmentMode)}
      >
        <Grid gap={4} align="stretch">
          <Grid.Column size={6}>
            <InputRadioCard
              value="single"
              showInputElement={false}
              additionalContent={
                <Text variant="body_2" color="neutral_alt" align="center">
                  {i18n.trans(t("flights_airline_portal_add_user_access_airline_subheader"))}
                </Text>
              }
            >
              <Stack direction="column" alignItems="center" gap={2}>
                <Icon svg={TransportAirplaneIcon} color="neutral_alt" />
                <Text variant="emphasized_1">
                  {i18n.trans(t("flights_airline_portal_add_user_access_airline_header"))}
                </Text>
              </Stack>
            </InputRadioCard>
          </Grid.Column>
          <Grid.Column size={6}>
            <InputRadioCard
              value="group"
              showInputElement={false}
              additionalContent={
                <Text variant="body_2" color="neutral_alt" align="center">
                  {i18n.trans(t("flights_airline_portal_add_user_access_group_subheader"))}
                </Text>
              }
            >
              <Stack direction="column" alignItems="center" gap={2}>
                <Icon svg={GroupIcon} color="neutral_alt" />
                <Text variant="emphasized_1">
                  {i18n.trans(t("flights_airline_portal_add_user_access_group_header"))}
                </Text>
              </Stack>
            </InputRadioCard>
          </Grid.Column>
        </Grid>
      </InputRadioGroup>

      {mode === "single" && (
        <SingleAirlineSection
          airlines={airlines}
          selectedAirlineId={selectedSingleAirlineId}
          onAirlineChange={onSingleAirlineChange}
        />
      )}

      {mode === "group" && (
        <GroupAccessSection
          groups={groups}
          airlines={airlines}
          selectedGroupId={selectedGroupId}
          selectedAirlineIds={selectedAirlineIds}
          onGroupChange={onGroupChange}
          onAirlineToggle={onAirlineToggle}
        />
      )}
    </Stack>
  );
};
